import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { LogIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";

export default function Login() {
  const { t } = useTranslation();
  const { login, user, checked } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/account";
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (checked && user) navigate(user.role === "admin" ? "/admin" : redirect, { replace: true });
  }, [checked, user, navigate, redirect]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : redirect, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("auth.loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex items-center justify-center bg-slate-50 px-5 py-28">
      <Seo noindex title="Sign In | Belo Rentals" path="/login" />
      <div className="w-full max-w-md">
        <form onSubmit={submit} data-testid="customer-login-form" className="bg-white rounded-2xl shadow-card p-8">
          <div className="flex items-center gap-3 text-slate-900 mb-2">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-blue-50 text-blue-600"><LogIn size={18} /></span>
            <h1 className="font-heading text-2xl font-semibold">{t("auth.welcomeBack")}</h1>
          </div>
          <p className="text-slate-500 text-sm mb-7">{t("auth.signInSub")}</p>

          {error && <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl mb-6">{error}</div>}

          <div className="space-y-5">
            <div>
              <span className="label-caps text-slate-400 block mb-2">{t("auth.email")}</span>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="belo-input" placeholder="you@email.com" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="label-caps text-slate-400">{t("auth.password")}</span>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-blue-600 text-xs font-semibold hover:underline">{t("auth.forgotPassword")}</Link>
              </div>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="belo-input" placeholder="••••••••" />
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="login-submit" className="mt-7 w-full bg-blue-600 text-white py-3.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
            {loading ? t("auth.signingIn") : t("auth.signIn")}
          </button>

          <p className="text-center text-sm text-slate-500 mt-6">
            {t("auth.newHere")}{" "}
            <Link to={`/register${redirect !== "/account" ? `?redirect=${redirect}` : ""}`} data-testid="go-register-link" className="text-blue-600 font-semibold hover:underline">{t("auth.createAccount")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
